import { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Play, BarChart2, Settings, MessageSquare, Users, BookmarkPlus, CreditCard } from 'lucide-react';

interface TourStep {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  targetElement?: string;
  position: 'center' | 'top' | 'bottom' | 'left' | 'right';
}

interface WelcomeTourProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const WelcomeTour = ({ isOpen, onClose, onComplete }: WelcomeTourProps) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const tourSteps: TourStep[] = [
    {
      id: 'coaching-discussion',
      title: 'Start Coaching Discussion',
      description: 'Begin your coaching journey by starting a conversation with a coach most appropriate for your needs. Ask questions, share challenges, and get personalized guidance. Generate Minutes of Meeting (MoM) at the end of the coaching session to capture key takeaways.',
      icon: MessageSquare,
      position: 'center'
    },
    {
      id: 'multiple-coaches',
      title: 'Multiple Coach Perspectives',
      description: 'Select up to 3 coaches for diverse viewpoints. Each coach brings unique expertise to help you see challenges from different angles.',
      icon: Users,
      position: 'center'
    },
    {
      id: 'action-ledger',
      title: 'Action Ledger',
      description: 'Save important insights to your Action Ledger. Click the bookmark icon next to valuable advice to build your personal growth library.',
      icon: BookmarkPlus,
      position: 'center'
    },
    {
      id: 'insights',
      title: 'Track Your Progress',
      description: 'Monitor your growth with personalized insights. View session trends, goal progress, and coaching engagement metrics.',
      icon: BarChart2,
      position: 'center'
    },
    {
      id: 'payments',
      title: 'Payments & Sessions',
      description: 'Manage your coaching sessions and subscription. Get started with 7 monthly sessions at ₹99.',
      icon: CreditCard,
      position: 'center'
    }
  ];

  const handleNext = async () => {
    if (currentStep < tourSteps.length - 1) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep + 1);
        setIsAnimating(false);
      }, 200);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = async () => {
    if (currentStep > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentStep(currentStep - 1);
        setIsAnimating(false);
      }, 200);
    }
  };

  const handleComplete = () => {
    onComplete();
    onClose();
  };

  const handleSkip = () => {
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setIsAnimating(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentTourStep = tourSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tourSteps.length - 1;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 transition-opacity duration-300" />

      {/* Tour Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-lg mx-auto">
          {/* Main Tour Card */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-300 scale-100">
            {/* Header */}
            <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 px-8 py-6">
              <button
                onClick={handleSkip}
                className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/60 text-gray-500 hover:text-gray-700 transition-colors"
                aria-label="Skip tour"
              >
                <X className="h-5 w-5" strokeWidth={1.5} />
              </button>

              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white shadow-sm">
                  <currentTourStep.icon className="h-6 w-6 text-royal-blue" strokeWidth={1.5} />
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold text-gray-900 tracking-tight">
                    {currentTourStep.title}
                  </h2>
                  <p className="text-sm font-display font-medium text-gray-600">
                    Step {currentStep + 1} of {tourSteps.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Contents */}
            <div className="px-8 py-8">
              <div className={`transition-all duration-200 ${isAnimating ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>
                <p className="font-body text-base text-gray-700 leading-relaxed mb-8">
                  {currentTourStep.description}
                </p>

                {/* Progress Indicators */}
                <div className="flex justify-center gap-2 mb-8">
                  {tourSteps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 rounded-full transition-all duration-300 ${index === currentStep
                        ? 'w-8 bg-royal-blue'
                        : index < currentStep
                          ? 'w-2 bg-royal-blue/60'
                          : 'w-2 bg-gray-200'
                        }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50/50 px-8 py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {!isFirstStep && (
                    <button
                      onClick={handlePrevious}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-display font-medium text-gray-600 hover:text-gray-900 hover:bg-white transition-all duration-200"
                    >
                      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                      Previous
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSkip}
                    className="px-4 py-2.5 rounded-2xl text-sm font-display font-medium text-gray-500 hover:text-gray-700 hover:bg-white transition-all duration-200"
                  >
                    Skip Tour
                  </button>

                  <button
                    onClick={handleNext}
                    className="inline-flex items-center gap-2 bg-coral px-6 py-2.5 rounded-2xl text-sm font-display font-semibold text-white shadow-sm hover:bg-coral-dark hover:shadow-md transition-all duration-200 min-h-[44px]"
                  >
                    <span className='text-white'>{isLastStep ? 'Get Started' : 'Next'}</span>
                    <ArrowRight className="h-4 w-4 text-white" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WelcomeTour;
